import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { PerfilBadge, StatusConviteBadge } from "@/components/status-badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ApiError, apiFetch } from "@/lib/api";
import { formatarDataHora } from "@/lib/format";
import type { ConviteListado, Perfil } from "@/lib/types";

const schema = z.object({
  email: z.string().email("E-mail inválido"),
  perfil: z.enum(["user", "premium", "admin"]),
  notes: z.string().optional(),
});
type Form = z.infer<typeof schema>;

export function ConvitesPage() {
  const qc = useQueryClient();
  const [abertoCriar, setAbertoCriar] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["convites"],
    queryFn: () => apiFetch<ConviteListado[]>("/api/convites"),
  });

  const criar = useMutation({
    mutationFn: (input: Form) => apiFetch("/api/convites", { method: "POST", body: input }),
    onSuccess: () => {
      toast.success("Convite enviado");
      qc.invalidateQueries({ queryKey: ["convites"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
      setAbertoCriar(false);
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Falha ao criar convite"),
  });

  const revogar = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/convites/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Convite revogado");
      qc.invalidateQueries({ queryKey: ["convites"] });
      qc.invalidateQueries({ queryKey: ["dashboard", "stats"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Falha ao revogar"),
  });

  const reenviar = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/convites/${id}/reenviar`, { method: "POST" }),
    onSuccess: () => {
      toast.success("Convite reenviado");
      qc.invalidateQueries({ queryKey: ["convites"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "Falha ao reenviar"),
  });

  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", perfil: "user", notes: "" },
  });

  function abrir(estado: boolean) {
    setAbertoCriar(estado);
    if (!estado) form.reset();
  }

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="font-display text-2xl font-semibold">Convites</h1>
          <p className="text-sm text-muted-foreground">Crie convites diretos e acompanhe o status de cada envio.</p>
        </div>
        <Dialog open={abertoCriar} onOpenChange={abrir}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="size-4" />
              Novo convite
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={form.handleSubmit((v) => criar.mutate(v))} className="flex flex-col gap-4">
              <DialogHeader>
                <DialogTitle>Novo convite</DialogTitle>
                <DialogDescription>O e-mail recebe um link de aceite válido por 7 dias.</DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">E-mail</Label>
                <Input id="email" type="email" autoComplete="off" maxLength={254} {...form.register("email")} />
                {form.formState.errors.email && (
                  <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="perfil">Perfil</Label>
                <Select
                  value={form.watch("perfil")}
                  onValueChange={(v) => form.setValue("perfil", v as Perfil)}
                >
                  <SelectTrigger id="perfil">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Usuário</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="notes">Observações (opcional)</Label>
                <Input id="notes" maxLength={500} {...form.register("notes")} />
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline">
                    Cancelar
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={criar.isPending}>
                  {criar.isPending ? "Enviando..." : "Enviar convite"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Convites enviados</CardTitle>
          <CardDescription>{data ? `${data.length} convite(s)` : "Carregando..."}</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-mail</TableHead>
                <TableHead>Perfil</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Expira em</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : data && data.length > 0 ? (
                data.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-medium">{c.email}</TableCell>
                    <TableCell>
                      <PerfilBadge perfil={c.perfil} />
                    </TableCell>
                    <TableCell>
                      <StatusConviteBadge status={c.status} />
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatarDataHora(c.expiresAt)}</TableCell>
                    <TableCell className="text-muted-foreground">{formatarDataHora(c.criadoEm)}</TableCell>
                    <TableCell>
                      {c.status === "pending" && (
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Reenviar convite"
                            disabled={reenviar.isPending}
                            onClick={() => reenviar.mutate(c.id)}
                            className="text-muted-foreground hover:text-primary"
                          >
                            <Mail className="size-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                title="Revogar convite"
                                className="text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Revogar convite?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  O link enviado para <strong>{c.email}</strong> deixará de funcionar. Não é possível desfazer.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  onClick={() => revogar.mutate(c.id)}
                                >
                                  Revogar
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    Nenhum convite ainda.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
